import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProducts, Product } from '@/hooks/useProducts';
import { useCart } from '@/hooks/useCart';

function CollectionProductCard({ product }: { product: Product }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const image = product.media?.[0]?.url ?? '';

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Variant products must pick options first — send them to the detail page.
    if (product.hasVariants) { navigate(`/product/${product.id}`); return; }
    addItem({
      productId: product.id,
      skuId: null,
      skuLabel: null,
      name: product.name,
      price: product.price,
      image,
    });
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.hasVariants) { navigate(`/product/${product.id}`); return; }
    addItem({
      productId: product.id,
      skuId: null,
      skuLabel: null,
      name: product.name,
      price: product.price,
      image,
    });
    navigate('/checkout');
  };

  return (
    <div className="group bg-white rounded-lg overflow-hidden border border-[#E8E0D4] hover:shadow-md transition-shadow flex flex-col">
      <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-white p-3 md:p-4">
        {image && (
          <img
            src={image}
            alt={product.name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        )}
      </Link>
      <div className="p-3 md:p-4 flex flex-col flex-1">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-sm md:text-base lg:text-lg font-semibold text-[#1A1A1A] mb-1 leading-snug group-hover:text-[#4f528d] transition-colors text-center">
            {product.name}
          </h3>
        </Link>
        <p className="text-base md:text-lg font-semibold text-[#2c2a4a] text-center mb-3">${product.price.toFixed(2)}</p>
        <div className="mt-auto grid grid-cols-2 gap-2">
          <button
            onClick={handleAddToCart}
            className="py-2 px-2 rounded-md border border-[#4f528d] text-[#4f528d] text-[11px] font-semibold uppercase tracking-tight hover:bg-[#4f528d] hover:text-white transition-colors whitespace-nowrap text-center"
          >
            Add to Cart
          </button>
          <button
            onClick={handleBuyNow}
            className="py-2 px-2 rounded-md bg-[#4f528d] text-white text-[11px] font-semibold uppercase tracking-tight hover:bg-[#3d4070] transition-colors whitespace-nowrap text-center"
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
}

interface CollectionPageProps {
  category: string;
  title: string;
  subtitle: string;
  heroImage: string;
  description: string;
  heroGradient?: boolean;
}

export default function CollectionPage({ category, title, subtitle, heroImage, heroGradient }: CollectionPageProps) {
  const { products, loading, error, fetchProducts } = useProducts();
  const [sort, setSort] = useState('default');

  useEffect(() => {
    fetchProducts(category, undefined, sort);
  }, [category, sort, fetchProducts]);

  return (
    <main className="min-h-screen bg-[#f0f4f7]">
      {/* Hero */}
      <section
        className={`relative overflow-hidden flex flex-col items-center justify-center text-center px-4 ${
          heroGradient
            ? 'bg-gradient-to-br from-[#2c2a4a] via-[#3d3b6e] to-[#2c2a4a] h-[160px] sm:h-[200px] md:h-[240px]'
            : 'h-[260px] sm:h-[320px] md:h-[420px]'
        }`}
      >
        {!heroGradient && (
          <>
            <img src={heroImage} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/20" />
          </>
        )}
        <div className="relative z-10">
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3 drop-shadow-lg">
            {title}
          </h1>
          <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-xl drop-shadow">{subtitle}</p>
        </div>
      </section>

      {/* Products */}
      <section className="py-6 md:py-10">
        <div className="container-main px-4">
          {/* Sort Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <p className="text-sm text-[#8B7355]">
              {products.length} product{products.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label htmlFor="collection-sort" className="text-sm text-[#8B7355] whitespace-nowrap">Sort by:</label>
                <select
                  id="collection-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="bg-white border border-[#D4C9B8] rounded-md py-2 px-3 text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#B87333] cursor-pointer"
                >
                  <option value="default">Featured</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-12 md:py-16">
              <div className="w-8 h-8 md:w-10 md:h-10 border-3 border-[#B87333] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#8B7355] text-sm md:text-base">Loading products...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-12 md:py-16">
              <p className="text-red-600 text-sm md:text-base">Error loading products. Please try again.</p>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && products.length === 0 && (
            <div className="text-center py-12 md:py-16">
              <i className="ri-shopping-basket-line text-3xl md:text-4xl text-[#D4C9B8] mb-3" />
              <p className="text-[#8B7355] text-base md:text-lg">No products in this collection yet.</p>
            </div>
          )}

          {/* Grid */}
          {!loading && !error && products.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5 lg:gap-6">
              {products.map((product) => (
                <CollectionProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}